import React, { useRef, useEffect, useMemo, Suspense, Component } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';

const MP = {
  NOSE: 0,
  LEFT_SHOULDER: 11, RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13, RIGHT_ELBOW: 14,
  LEFT_WRIST: 15, RIGHT_WRIST: 16,
  LEFT_HIP: 23, RIGHT_HIP: 24,
  LEFT_KNEE: 25, RIGHT_KNEE: 26,
  LEFT_ANKLE: 27, RIGHT_ANKLE: 28,
};

// Bone names (Three.js strips colons from Mixamo names)
const B = {
  Hips: 'mixamorig1Hips',
  Spine: 'mixamorig1Spine',
  Head: 'mixamorig1Head',
  LeftArm: 'mixamorig1LeftArm',
  LeftForeArm: 'mixamorig1LeftForeArm',
  RightArm: 'mixamorig1RightArm',
  RightForeArm: 'mixamorig1RightForeArm',
  LeftUpLeg: 'mixamorig1LeftUpLeg',
  LeftLeg: 'mixamorig1LeftLeg',
  RightUpLeg: 'mixamorig1RightUpLeg',
  RightLeg: 'mixamorig1RightLeg',
};

const VIS_THRESHOLD = 0.3;
const SMOOTH_ALPHA = 0.3;

const getLm = (landmarks, idx) => {
  const p = landmarks[idx];
  if (!p || (p.visibility ?? 0) < VIS_THRESHOLD) return null;
  return p;
};

const MODEL_PATH = `${process.env.PUBLIC_URL}/models/Ch45_nonPBR.glb`;

const _euler = new THREE.Euler();
const _quat = new THREE.Quaternion();

function AvatarModel({ landmarks }) {
  const { scene } = useGLTF(MODEL_PATH);
  const groupRef = useRef();
  const landmarksRef = useRef(landmarks);
  const smoothedRef = useRef({});

  landmarksRef.current = landmarks;

  const { bones, restPose, skeleton } = useMemo(() => {
    const boneMap = {};
    const restMap = {};
    let skel = null;

    scene.traverse((child) => {
      if (child.isSkinnedMesh && child.skeleton) {
        skel = child.skeleton;
        child.frustumCulled = false;
      }
    });

    if (skel) {
      skel.bones.forEach((bone) => {
        boneMap[bone.name] = bone;
        restMap[bone.name] = bone.quaternion.clone();
      });
    }

    return { bones: boneMap, restPose: restMap, skeleton: skel };
  }, [scene]);

  const smooth = (key, target) => {
    const prev = smoothedRef.current[key];
    if (prev === undefined) {
      smoothedRef.current[key] = target;
      return target;
    }
    const s = prev + (target - prev) * SMOOTH_ALPHA;
    smoothedRef.current[key] = s;
    return s;
  };

  const setBone = (boneName, x, y, z) => {
    const bone = bones[boneName];
    const rest = restPose[boneName];
    if (!bone || !rest) return;
    _euler.set(x, y, z, 'XYZ');
    _quat.setFromEuler(_euler);
    bone.quaternion.copy(rest).multiply(_quat);
  };

  useFrame(() => {
    if (!bones[B.Hips]) return;

    const lms = landmarksRef.current;
    if (!lms || lms.length === 0) return;

    const ls = getLm(lms, MP.LEFT_SHOULDER);
    const rs = getLm(lms, MP.RIGHT_SHOULDER);
    const le = getLm(lms, MP.LEFT_ELBOW);
    const re = getLm(lms, MP.RIGHT_ELBOW);
    const lw = getLm(lms, MP.LEFT_WRIST);
    const rw = getLm(lms, MP.RIGHT_WRIST);
    const lhip = getLm(lms, MP.LEFT_HIP);
    const rhip = getLm(lms, MP.RIGHT_HIP);
    const lk = getLm(lms, MP.LEFT_KNEE);
    const rk = getLm(lms, MP.RIGHT_KNEE);
    const la = getLm(lms, MP.LEFT_ANKLE);
    const ra = getLm(lms, MP.RIGHT_ANKLE);
    const nose = getLm(lms, MP.NOSE);

    // Safe z accessor (MediaPipe z = depth, negative = closer to camera)
    const pz = (p) => (p && p.z) || 0;

    // === HIPS: tilt (Z) + turn (Y) from depth ===
    if (lhip && rhip) {
      const hipTiltZ = Math.atan2(rhip.y - lhip.y, rhip.x - lhip.x);
      const hipDx = rhip.x - lhip.x;
      const hipDz = pz(rhip) - pz(lhip);
      const hipTurnY = Math.atan2(hipDz, Math.abs(hipDx) || 0.1);
      setBone(B.Hips,
        0,
        smooth('hipsY', hipTurnY * 0.6),
        smooth('hipsZ', -hipTiltZ * 0.3)
      );
    }

    // === SPINE: lean forward/back (X) + turn (Y) + side lean (Z) ===
    if (ls && rs && lhip && rhip) {
      const sMidX = (ls.x + rs.x) / 2, hMidX = (lhip.x + rhip.x) / 2;
      const sMidY = (ls.y + rs.y) / 2, hMidY = (lhip.y + rhip.y) / 2;
      const sMidZ = (pz(ls) + pz(rs)) / 2, hMidZ = (pz(lhip) + pz(rhip)) / 2;
      const torsoLen = Math.abs(sMidY - hMidY) || 0.3;

      const leanX = Math.atan2(sMidZ - hMidZ, torsoLen);
      const leanZ = -Math.atan2(sMidX - hMidX, torsoLen);
      const shoulderDz = pz(rs) - pz(ls);
      const shoulderDx = Math.abs(rs.x - ls.x) || 0.1;
      const turnY = Math.atan2(shoulderDz, shoulderDx);

      setBone(B.Spine,
        smooth('spineX', leanX * 0.6),
        smooth('spineY', turnY * 0.5),
        smooth('spineZ', leanZ * 0.5)
      );
    }

    // === LEFT ARM: screen angle (Z) + depth forward/back (X) ===
    if (ls && le) {
      const dx = -(le.x - ls.x), dy = le.y - ls.y;
      const dz = pz(le) - pz(ls);
      const screenAngle = Math.atan2(dy, dx);
      const depthAngle = Math.atan2(dz, Math.sqrt(dx * dx + dy * dy) || 0.1);
      setBone(B.LeftArm,
        smooth('leftArmX', depthAngle * 1.2),
        0,
        smooth('leftArmZ', screenAngle)
      );
    }

    // === LEFT FOREARM ===
    if (ls && le && lw) {
      const uaDx = -(le.x - ls.x), uaDy = le.y - ls.y;
      const faDx = -(lw.x - le.x), faDy = lw.y - le.y;
      const faDz = pz(lw) - pz(le);
      const uaAngle = Math.atan2(uaDy, uaDx);
      const faAngle = Math.atan2(faDy, faDx);
      const depthAngle = Math.atan2(faDz, Math.sqrt(faDx * faDx + faDy * faDy) || 0.1);
      setBone(B.LeftForeArm,
        smooth('leftForeArmX', depthAngle),
        0,
        smooth('leftForeArmZ', faAngle - uaAngle)
      );
    }

    // === RIGHT ARM: screen angle (Z) + depth forward/back (X) ===
    if (rs && re) {
      const dx = -(re.x - rs.x), dy = re.y - rs.y;
      const dz = pz(re) - pz(rs);
      const screenAngle = Math.atan2(dy, dx) - Math.PI;
      const depthAngle = Math.atan2(dz, Math.sqrt(dx * dx + dy * dy) || 0.1);
      setBone(B.RightArm,
        smooth('rightArmX', -depthAngle * 1.2),
        0,
        smooth('rightArmZ', screenAngle)
      );
    }

    // === RIGHT FOREARM ===
    if (rs && re && rw) {
      const uaDx = -(re.x - rs.x), uaDy = re.y - rs.y;
      const faDx = -(rw.x - re.x), faDy = rw.y - re.y;
      const faDz = pz(rw) - pz(re);
      const uaAngle = Math.atan2(uaDy, uaDx);
      const faAngle = Math.atan2(faDy, faDx);
      const depthAngle = Math.atan2(faDz, Math.sqrt(faDx * faDx + faDy * faDy) || 0.1);
      setBone(B.RightForeArm,
        smooth('rightForeArmX', -depthAngle),
        0,
        smooth('rightForeArmZ', faAngle - uaAngle)
      );
    }

    // === LEFT LEG: screen (Z) + depth (X) ===
    if (lhip && lk) {
      const dx = -(lk.x - lhip.x), dy = lk.y - lhip.y;
      const dz = pz(lk) - pz(lhip);
      const screenAngle = Math.atan2(dx, dy);
      const depthAngle = Math.atan2(dz, dy || 0.1);
      setBone(B.LeftUpLeg,
        smooth('leftUpLegX', depthAngle * 0.8),
        0,
        smooth('leftUpLegZ', screenAngle * 0.8)
      );
    }
    if (lhip && lk && la) {
      const uaDy = lk.y - lhip.y, uaDx = -(lk.x - lhip.x);
      const laDy = la.y - lk.y, laDx = -(la.x - lk.x);
      const laDz = pz(la) - pz(lk);
      const uaAngle = Math.atan2(uaDy, uaDx);
      const laAngle = Math.atan2(laDy, laDx);
      const depthAngle = Math.atan2(laDz, laDy || 0.1);
      setBone(B.LeftLeg,
        smooth('leftLegX', depthAngle * 0.6),
        0,
        smooth('leftLegZ', (laAngle - uaAngle) * 0.8)
      );
    }

    // === RIGHT LEG: screen (Z) + depth (X) ===
    if (rhip && rk) {
      const dx = -(rk.x - rhip.x), dy = rk.y - rhip.y;
      const dz = pz(rk) - pz(rhip);
      const screenAngle = Math.atan2(dx, dy);
      const depthAngle = Math.atan2(dz, dy || 0.1);
      setBone(B.RightUpLeg,
        smooth('rightUpLegX', depthAngle * 0.8),
        0,
        smooth('rightUpLegZ', screenAngle * 0.8)
      );
    }
    if (rhip && rk && ra) {
      const uaDy = rk.y - rhip.y, uaDx = -(rk.x - rhip.x);
      const raDy = ra.y - rk.y, raDx = -(ra.x - rk.x);
      const raDz = pz(ra) - pz(rk);
      const uaAngle = Math.atan2(uaDy, uaDx);
      const raAngle = Math.atan2(raDy, raDx);
      const depthAngle = Math.atan2(raDz, raDy || 0.1);
      setBone(B.RightLeg,
        smooth('rightLegX', depthAngle * 0.6),
        0,
        smooth('rightLegZ', (raAngle - uaAngle) * 0.8)
      );
    }

    // === HEAD: tilt (Z) + nod depth (X) ===
    if (nose && ls && rs) {
      const sMidX = (ls.x + rs.x) / 2;
      const sMidZ = (pz(ls) + pz(rs)) / 2;
      const sw = Math.abs(rs.x - ls.x) || 0.1;
      const headTurnZ = -(nose.x - sMidX) / sw;
      const headTiltX = (pz(nose) - sMidZ) / (sw || 0.1);
      setBone(B.Head,
        smooth('headX', headTiltX * 0.4),
        0,
        smooth('headZ', headTurnZ * 0.5)
      );
    }

    if (skeleton) skeleton.update();
  });

  return (
    <primitive
      ref={groupRef}
      object={scene}
      scale={1}
      position={[0, -0.92, 0]}
    />
  );
}

function CameraSetup() {
  const { camera } = useThree();
  useEffect(() => {
    camera.position.set(0, 0.5, 2.5);
    camera.lookAt(0, 0.5, 0);
    camera.updateProjectionMatrix();
  }, [camera]);
  return null;
}

const LoadingFallback = () => (
  <mesh position={[0, 0.5, 0]}>
    <boxGeometry args={[0.3, 0.3, 0.3]} />
    <meshStandardMaterial color="#00E5FF" wireframe />
  </mesh>
);

class CanvasErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('Avatar3D crashed:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="absolute inset-0 w-full h-full flex items-center justify-center"
          style={{ zIndex: 10, background: '#080810', color: '#00E5FF' }}>
          <div className="text-center">
            <div className="text-lg font-bold mb-2">3D Avatar Error</div>
            <div className="text-sm text-gray-400">{this.state.error?.message || 'WebGL context lost'}</div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const Avatar3D = ({ landmarks }) => (
  <CanvasErrorBoundary>
    <div className="absolute inset-0 w-full h-full"
      style={{ zIndex: 10, pointerEvents: 'none', background: '#080810' }}
      data-testid="avatar-3d-container">
      <Canvas
        gl={{ antialias: true, alpha: false }}
        dpr={[1, 1.5]}
        camera={{ fov: 50, near: 0.1, far: 100 }}
        onCreated={({ gl }) => { gl.outputColorSpace = THREE.SRGBColorSpace; }}
      >
        <CameraSetup />
        <ambientLight intensity={0.8} />
        <directionalLight position={[2, 3, 4]} intensity={1.5} />
        <directionalLight position={[-2, 1, -2]} intensity={0.6} />
        <hemisphereLight color="#ffffff" groundColor="#444444" intensity={0.5} />
        <pointLight position={[0, 2, 2]} intensity={0.8} color="#00E5FF" />
        <Suspense fallback={<LoadingFallback />}>
          <AvatarModel landmarks={landmarks} />
        </Suspense>
      </Canvas>
    </div>
  </CanvasErrorBoundary>
);

useGLTF.preload(MODEL_PATH);

export default React.memo(Avatar3D);
